import type {
  PersonInput,
  ChildInput,
  ContributionInputType,
  PensionType,
  CharityType,
  ShareholderInput,
} from '@/types';

/**
 * Parse a string value to a number, returning undefined if invalid.
 * Uses strict validation - rejects partial matches like '12abc', Infinity, and whitespace.
 */
export function parseNumber(value: string | null): number | undefined {
  // Reject null, empty, or whitespace-only strings (Number() returns 0 for these)
  if (value === null || value.trim() === '') return undefined;
  // Number() returns NaN for invalid or partially invalid strings like '12abc',
  // unlike parseFloat() which would return 12
  const num = Number(value);
  // Reject NaN and Infinity (which Number() accepts for 'Infinity'/'-Infinity')
  if (isNaN(num) || !isFinite(num)) return undefined;
  return num;
}

/**
 * Parse a string value to a boolean, returning undefined if invalid.
 * Accepts: 'true', '1', 'yes' as true; 'false', '0', 'no' as false.
 */
export function parseBoolean(value: string | null): boolean | undefined {
  if (value === null || value === '') return undefined;
  const lower = value.toLowerCase();
  if (lower === 'true' || lower === '1' || lower === 'yes') return true;
  if (lower === 'false' || lower === '0' || lower === 'no') return false;
  return undefined;
}

/**
 * Parse a string value to an enum value, returning undefined if not in allowed list.
 * Case-insensitive matching - e.g., 'SALARY_SACRIFICE' matches 'salary_sacrifice'.
 */
export function parseEnum<T extends string>(
  value: string | null,
  allowed: readonly T[]
): T | undefined {
  if (value === null || value === '') return undefined;
  const lower = value.toLowerCase();
  return allowed.find((a) => a.toLowerCase() === lower) as T | undefined;
}

/**
 * Parse a comma-separated string of numbers into an array (for children ages).
 * Uses strict validation consistent with parseNumber. Returns empty array if invalid.
 */
export function parseNumberArray(value: string | null): number[] {
  if (value === null || value === '') return [];
  return value
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '') // Filter empty entries before Number() (which returns 0 for '')
    .map((v) => Number(v))
    .filter((n) => !isNaN(n) && isFinite(n) && n >= 0);
}

// Allowed enum values
const CONTRIBUTION_TYPES: readonly ContributionInputType[] = ['amount', 'percentage'];
const PENSION_TYPES: readonly PensionType[] = ['salary_sacrifice', 'relief_at_source', 'net_pay'];
const CHARITY_TYPES: readonly CharityType[] = ['gift_aid', 'payroll_giving'];

/**
 * Parsed calculator (household) query parameters.
 */
export interface ParsedCalculatorParams {
  primary: Partial<PersonInput>;
  partner?: Partial<PersonInput>;
  children?: ChildInput[];
  claimsChildBenefit?: boolean;
  includePartner?: boolean;
}

/**
 * Parse query parameters for the Calculator page.
 */
export function parseCalculatorParams(params: URLSearchParams): ParsedCalculatorParams {
  const result: ParsedCalculatorParams = {
    primary: {},
  };

  // Primary person fields
  const salary = parseNumber(params.get('salary'));
  if (salary !== undefined) result.primary.gross_salary = salary;

  const taxCode = params.get('tax_code');
  if (taxCode) result.primary.tax_code = taxCode;

  const pension = parseNumber(params.get('pension'));
  if (pension !== undefined) result.primary.net_pension_contributions_amount = pension;

  const pensionPct = parseNumber(params.get('pension_pct'));
  if (pensionPct !== undefined) result.primary.net_pension_contributions_percentage = pensionPct;

  const pensionMode = parseEnum(params.get('pension_mode'), CONTRIBUTION_TYPES);
  if (pensionMode !== undefined) result.primary.net_pension_contributions_type = pensionMode;

  const pensionType = parseEnum(params.get('pension_type'), PENSION_TYPES);
  if (pensionType !== undefined) result.primary.pension_type = pensionType;

  const employerPension = parseNumber(params.get('employer_pension'));
  if (employerPension !== undefined) result.primary.employer_pension_contributions_amount = employerPension;

  const employerPensionPct = parseNumber(params.get('employer_pension_pct'));
  if (employerPensionPct !== undefined) result.primary.employer_pension_contributions_percentage = employerPensionPct;

  const employerPensionMode = parseEnum(params.get('employer_pension_mode'), CONTRIBUTION_TYPES);
  if (employerPensionMode !== undefined) result.primary.employer_pension_contributions_type = employerPensionMode;

  const charity = parseNumber(params.get('charity'));
  const charityType = parseEnum(params.get('charity_type'), CHARITY_TYPES);
  if (charity !== undefined) {
    if (charityType === 'payroll_giving') {
      result.primary.payroll_giving_donations = charity;
    } else {
      result.primary.gift_aid_donations = charity;
    }
  }
  if (charityType !== undefined) result.primary.charity_type = charityType;

  const benefits = parseNumber(params.get('benefits'));
  if (benefits !== undefined) result.primary.taxable_benefits = benefits;

  const bonus = parseNumber(params.get('bonus'));
  if (bonus !== undefined) result.primary.bonus = bonus;

  const sacrifice = parseNumber(params.get('sacrifice'));
  if (sacrifice !== undefined) result.primary.salary_sacrifice_contributions = sacrifice;

  // Children
  const childrenAges = parseNumberArray(params.get('children'));
  if (childrenAges.length > 0) {
    result.children = childrenAges.map((age) => ({ age }));
  }

  const childBenefit = parseBoolean(params.get('child_benefit'));
  if (childBenefit !== undefined) result.claimsChildBenefit = childBenefit;

  // Partner
  const includePartner = parseBoolean(params.get('partner'));
  if (includePartner !== undefined) result.includePartner = includePartner;

  // Partner fields
  const partnerSalary = parseNumber(params.get('partner_salary'));
  const partnerTaxCode = params.get('partner_tax_code');
  const partnerBonus = parseNumber(params.get('partner_bonus'));
  const partnerPension = parseNumber(params.get('partner_pension'));
  const partnerPensionPct = parseNumber(params.get('partner_pension_pct'));
  const partnerPensionMode = parseEnum(params.get('partner_pension_mode'), CONTRIBUTION_TYPES);
  const partnerPensionType = parseEnum(params.get('partner_pension_type'), PENSION_TYPES);

  // Only create partner object if there are partner params
  if (
    partnerSalary !== undefined ||
    partnerTaxCode ||
    partnerBonus !== undefined ||
    partnerPension !== undefined ||
    partnerPensionPct !== undefined ||
    partnerPensionMode !== undefined ||
    partnerPensionType !== undefined
  ) {
    result.partner = {};
    if (partnerSalary !== undefined) result.partner.gross_salary = partnerSalary;
    if (partnerTaxCode) result.partner.tax_code = partnerTaxCode;
    if (partnerBonus !== undefined) result.partner.bonus = partnerBonus;
    if (partnerPension !== undefined) result.partner.net_pension_contributions_amount = partnerPension;
    if (partnerPensionPct !== undefined) result.partner.net_pension_contributions_percentage = partnerPensionPct;
    if (partnerPensionMode !== undefined) result.partner.net_pension_contributions_type = partnerPensionMode;
    if (partnerPensionType !== undefined) result.partner.pension_type = partnerPensionType;

    // Auto-enable partner if partner fields are present
    if (result.includePartner === undefined) {
      result.includePartner = true;
    }
  }

  return result;
}

/**
 * Parsed contractor query parameters.
 */
export interface ParsedContractorParams {
  // Company
  annualRevenue?: number;
  businessExpenses?: number;
  retainedProfits?: number;
  totalPensionContribution?: number;
  eligibleForEmploymentAllowance?: boolean;
  associatedCompanies?: number;

  // VAT
  vatRegistered?: boolean;
  vatUsingFlatRateScheme?: boolean;
  vatFlatRatePercentage?: number;

  // Director
  director?: Partial<ShareholderInput>;

  // Partner
  includePartner?: boolean;
  partner?: Partial<ShareholderInput>;

  // Children
  children?: ChildInput[];
  claimsChildBenefit?: boolean;
}

/**
 * Parse query parameters for the Contractor page.
 */
export function parseContractorParams(params: URLSearchParams): ParsedContractorParams {
  const result: ParsedContractorParams = {};

  // Company
  const revenue = parseNumber(params.get('revenue'));
  if (revenue !== undefined) result.annualRevenue = revenue;

  const expenses = parseNumber(params.get('expenses'));
  if (expenses !== undefined) result.businessExpenses = expenses;

  const retained = parseNumber(params.get('retained'));
  if (retained !== undefined) result.retainedProfits = retained;

  const companyPension = parseNumber(params.get('company_pension'));
  if (companyPension !== undefined) result.totalPensionContribution = companyPension;

  const employmentAllowance = parseBoolean(params.get('employment_allowance'));
  if (employmentAllowance !== undefined) result.eligibleForEmploymentAllowance = employmentAllowance;

  const associated = parseNumber(params.get('associated'));
  if (associated !== undefined) result.associatedCompanies = associated;

  // VAT
  const vat = parseBoolean(params.get('vat'));
  if (vat !== undefined) result.vatRegistered = vat;

  const vatFlatRate = parseBoolean(params.get('vat_flat_rate'));
  if (vatFlatRate !== undefined) result.vatUsingFlatRateScheme = vatFlatRate;

  const vatPct = parseNumber(params.get('vat_pct'));
  if (vatPct !== undefined) result.vatFlatRatePercentage = vatPct;

  // Director
  const salary = parseNumber(params.get('salary'));
  const taxCode = params.get('tax_code');
  const shareholding = parseNumber(params.get('shareholding'));
  const pension = parseNumber(params.get('pension'));
  const pensionPct = parseNumber(params.get('pension_pct'));
  const pensionMode = parseEnum(params.get('pension_mode'), CONTRIBUTION_TYPES);
  const pensionType = parseEnum(params.get('pension_type'), PENSION_TYPES);
  const charity = parseNumber(params.get('charity'));
  const charityType = parseEnum(params.get('charity_type'), CHARITY_TYPES);
  const selfEmployment = parseNumber(params.get('self_employment'));
  const otherIncome = parseNumber(params.get('other_income'));

  if (
    salary !== undefined ||
    taxCode ||
    shareholding !== undefined ||
    pension !== undefined ||
    pensionPct !== undefined ||
    pensionMode !== undefined ||
    pensionType !== undefined ||
    charity !== undefined ||
    charityType !== undefined ||
    selfEmployment !== undefined ||
    otherIncome !== undefined
  ) {
    result.director = {};
    if (salary !== undefined) result.director.gross_salary = salary;
    if (taxCode) result.director.tax_code = taxCode;
    if (shareholding !== undefined) result.director.shareholding_percentage = shareholding;
    if (pension !== undefined) result.director.net_pension_contributions_amount = pension;
    if (pensionPct !== undefined) result.director.net_pension_contributions_percentage = pensionPct;
    if (pensionMode !== undefined) result.director.net_pension_contributions_type = pensionMode;
    if (pensionType !== undefined) result.director.pension_type = pensionType;
    if (charity !== undefined) {
      if (charityType === 'payroll_giving') {
        result.director.payroll_giving_donations = charity;
      } else {
        result.director.gift_aid_donations = charity;
      }
    }
    if (charityType !== undefined) result.director.charity_type = charityType;
    if (selfEmployment !== undefined) result.director.self_employment_income = selfEmployment;
    if (otherIncome !== undefined) result.director.other_income = otherIncome;
  }

  // Partner
  const includePartner = parseBoolean(params.get('partner'));
  if (includePartner !== undefined) result.includePartner = includePartner;

  const partnerSalary = parseNumber(params.get('partner_salary'));
  const partnerShareholding = parseNumber(params.get('partner_shareholding'));

  if (partnerSalary !== undefined || partnerShareholding !== undefined) {
    result.partner = {};
    if (partnerSalary !== undefined) result.partner.gross_salary = partnerSalary;
    if (partnerShareholding !== undefined) result.partner.shareholding_percentage = partnerShareholding;

    // Auto-enable partner if partner fields are present
    if (result.includePartner === undefined) {
      result.includePartner = true;
    }
  }

  // Children
  const childrenAges = parseNumberArray(params.get('children'));
  if (childrenAges.length > 0) {
    result.children = childrenAges.map((age) => ({ age }));
  }

  const childBenefit = parseBoolean(params.get('child_benefit'));
  if (childBenefit !== undefined) result.claimsChildBenefit = childBenefit;

  return result;
}
